import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, ShoppingCart, Zap, AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import LottoField from '../components/LottoField';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL, Connection } from '@solana/web3.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reliable RPC endpoint for transactions
const MAINNET_RPC = 'https://api.mainnet-beta.solana.com';
const DEVNET_RPC = 'https://api.devnet.solana.com';

const Play = () => {
  const { connection } = useConnection();
  const { publicKey, sendTransaction, connected, wallet } = useWallet();
  
  const [settings, setSettings] = useState(null);
  const [fields, setFields] = useState(
    Array.from({ length: 8 }, (_, i) => ({
      field_number: i + 1,
      selected_numbers: [],
      is_active: i === 0,
    }))
  );
  const [purchasing, setPurchasing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [solBalance, setSolBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (connected && publicKey) {
      fetchBalance();
      registerUser();
    }
  }, [connected, publicKey]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const registerUser = async () => {
    if (!publicKey) return;
    try {
      await axios.post(`${API}/wallet/connect`, { 
        wallet_address: publicKey.toString() 
      });
    } catch (error) {
      console.error('Error registering user:', error);
    }
  };

  const fetchBalance = async () => {
    if (!publicKey) {
      console.log('Cannot fetch balance: publicKey missing');
      return;
    }
    
    setLoadingBalance(true);
    try {
      console.log('Fetching balance for:', publicKey.toString());
      
      // Fetch balance via backend API (avoids CORS issues)
      const response = await axios.get(`${API}/balance/${publicKey.toString()}`);
      const balance = response.data.balance_sol;
      
      console.log('Balance fetched:', balance, 'SOL');
      setSolBalance(balance);
    } catch (error) {
      console.error('Error fetching balance:', error.response?.data || error.message);
      setSolBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleNumberSelect = (fieldNumber, numbers) => {
    setFields(prev =>
      prev.map(field =>
        field.field_number === fieldNumber
          ? { ...field, selected_numbers: numbers }
          : field
      )
    );
  };

  const handleToggleField = (fieldNumber) => {
    setFields(prev =>
      prev.map(field =>
        field.field_number === fieldNumber
          ? { ...field, is_active: !field.is_active }
          : field
      )
    );
  };

  const activeFields = fields.filter(f => f.is_active);
  const validFields = activeFields.filter(f => f.selected_numbers.length === 6);
  const pricePerField = settings?.price_per_field || 1;
  const solUsdcRate = settings?.sol_usdc_rate || 200;
  const adminWallet = settings?.admin_wallet || '';
  const network = settings?.network || 'devnet';
  
  const totalUsdc = validFields.length * pricePerField;
  const totalSol = totalUsdc / solUsdcRate;
  const pricePerFieldSol = pricePerField / solUsdcRate;

  const handleQuickPickAll = () => {
    setFields(prev =>
      prev.map(field => {
        if (!field.is_active) return field;
        const randomNumbers = [];
        while (randomNumbers.length < 6) {
          const rand = Math.floor(Math.random() * 49) + 1;
          if (!randomNumbers.includes(rand)) {
            randomNumbers.push(rand);
          }
        }
        return { ...field, selected_numbers: randomNumbers.sort((a, b) => a - b) };
      })
    );
  };

  const handleClearAll = () => {
    setFields(prev =>
      prev.map(field => ({ ...field, selected_numbers: [] }))
    );
  };

  const handlePurchase = async () => {
    if (!connected || !publicKey) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (validFields.length === 0) {
      toast.error('Please select at least 6 numbers in an active field');
      return;
    }

    if (!adminWallet) {
      toast.error('Lottery is not configured. Please contact admin.');
      return;
    }

    // Check balance
    if (solBalance !== null && solBalance < totalSol) {
      toast.error(`Insufficient balance. You have ${solBalance.toFixed(4)} SOL but need ${totalSol.toFixed(4)} SOL`);
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmPurchase = async () => {
    setPurchasing(true);
    setShowConfirmDialog(false);

    try {
      toast.info('Preparing transaction...');
      
      // Create transaction
      const toPubkey = new PublicKey(adminWallet);
      const lamports = Math.round(totalSol * LAMPORTS_PER_SOL);
      
      // Get blockhash from backend (avoids CORS issues)
      toast.info('Getting blockhash...');
      const blockhashResponse = await axios.get(`${API}/blockhash?network=${network}`);
      const { blockhash } = blockhashResponse.data;
      
      // Build transaction
      const transaction = new Transaction({
        recentBlockhash: blockhash,
        feePayer: publicKey,
      }).add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey,
          lamports,
        })
      );

      toast.info('Please approve in your wallet...');
      
      // Use wallet adapter's sendTransaction (properly authorized)
      const signature = await sendTransaction(transaction, connection);
      
      toast.success(`Transaction sent! TX: ${signature.slice(0, 8)}...`);
      
      // Save ticket to backend
      const ticketData = {
        wallet_address: publicKey.toString(),
        fields: validFields.map(f => ({
          field_number: f.field_number,
          selected_numbers: f.selected_numbers,
          is_active: true,
        })),
        total_sol: totalSol,
        tx_signature: signature,
      };

      await axios.post(`${API}/tickets`, ticketData);
      
      toast.success('Ticket purchased successfully!');
      
      // Refresh balance
      fetchBalance();
      
      // Reset fields
      setFields(
        Array.from({ length: 8 }, (_, i) => ({
          field_number: i + 1,
          selected_numbers: [],
          is_active: i === 0,
        }))
      );
      
    } catch (error) {
      console.error('=== PAYMENT ERROR ===');
      console.error('Error:', error);
      console.error('Message:', error.message);
      console.error('Name:', error.name);
      console.error('Stack:', error.stack);
      console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      
      const errorMsg = error.message || error.toString();
      
      // Show error for 30 seconds so user can read it
      const toastOptions = { duration: 30000 };
      
      if (errorMsg.includes('User rejected') || errorMsg.includes('rejected')) {
        toast.error('Transaction cancelled by user', toastOptions);
      } else if (errorMsg.includes('insufficient') || errorMsg.includes('Insufficient')) {
        toast.error('Insufficient SOL balance', toastOptions);
      } else if (errorMsg.includes('blockhash') || errorMsg.includes('Blockhash')) {
        toast.error('Blockhash error - try again', toastOptions);
      } else {
        // Show full error message
        toast.error(`Error: ${errorMsg}`, toastOptions);
      }
      
      // Also show in an alert so it doesn't disappear
      alert(`Payment Error:\n\n${errorMsg}\n\nCheck browser console (F12) for details.`);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-32" data-testid="play-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Lottery Ticket
            </h1>
            <span className={`px-2 py-1 rounded text-xs font-bold ${network === 'mainnet' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              {network === 'mainnet' ? 'MAINNET' : 'DEVNET'}
            </span>
          </div>
          <p className="text-white/60">
            Pick 6 numbers from 49 in up to 8 fields
          </p>
        </motion.div>

        {/* Wallet Balance */}
        {connected && publicKey && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-sol-purple/10 border border-sol-purple/30 rounded-xl p-4 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wallet className="w-5 h-5 text-sol-purple" />
                <div>
                  <p className="text-white/60 text-sm">Your Balance</p>
                  <p className="font-mono font-bold text-lg">
                    {loadingBalance ? (
                      <span className="text-white/40">Loading...</span>
                    ) : solBalance !== null ? (
                      <span className="text-sol-green">{solBalance.toFixed(4)} SOL</span>
                    ) : (
                      <span className="text-white/40">--</span>
                    )}
                    {solBalance !== null && solUsdcRate && (
                      <span className="text-white/40 text-sm ml-2">
                        ≈ ${(solBalance * solUsdcRate).toFixed(2)} USD
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={fetchBalance}
                disabled={loadingBalance}
                className="p-2 text-white/40 hover:text-white transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${loadingBalance ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Warnings */}
        {!adminWallet && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-red-400 text-sm">Lottery not configured. Admin wallet not set.</p>
          </motion.div>
        )}

        {/* Not Connected Warning */}
        {!connected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-sol-purple/10 border border-sol-purple/30 rounded-xl p-6 mb-8 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <AlertCircle className="w-6 h-6 text-sol-purple" />
              <div>
                <p className="font-bold">Wallet not connected</p>
                <p className="text-white/60 text-sm">Connect your wallet to buy tickets</p>
              </div>
            </div>
            <WalletMultiButton className="!bg-sol-green !text-black !rounded-lg !h-10" />
          </motion.div>
        )}

        {/* Quick Actions & Price Info */}
        <div className="flex flex-wrap gap-4 mb-8 items-center">
          <button
            onClick={handleQuickPickAll}
            className="btn-secondary flex items-center gap-2"
            data-testid="quickpick-all-btn"
          >
            <Zap className="w-4 h-4" />
            Quick Pick All
          </button>
          <button
            onClick={handleClearAll}
            className="text-white/40 hover:text-white/60 text-sm px-4 py-2 transition-colors"
            data-testid="clear-all-btn"
          >
            Clear All
          </button>
          
          <div className="ml-auto text-right">
            <p className="text-white/40 text-sm">Price per Field</p>
            <p className="font-mono text-sol-green font-bold">
              {pricePerField} USDC
              <span className="text-white/40 text-sm ml-2">
                ≈ {pricePerFieldSol.toFixed(4)} SOL
              </span>
            </p>
          </div>
        </div>

        {/* Lotto Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {fields.map(field => (
            <LottoField
              key={field.field_number}
              fieldNumber={field.field_number}
              selectedNumbers={field.selected_numbers}
              isActive={field.is_active}
              onToggle={handleToggleField}
              onNumberSelect={handleNumberSelect}
            />
          ))}
        </div>

        {/* Purchase Summary - Fixed at Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-[#0F172A]/95 backdrop-blur-xl border-t border-white/10 p-4"
        >
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-white/60 text-sm">
                {validFields.length} of {activeFields.length} fields completed
              </p>
              <div className="flex items-center gap-4">
                <p className="font-heading text-2xl font-bold">
                  Total: <span className="text-sol-green">{totalUsdc.toFixed(2)} USDC</span>
                </p>
                <span className="text-white/40">≈</span>
                <p className="font-mono text-lg text-white/60">
                  {totalSol.toFixed(4)} SOL
                </p>
              </div>
            </div>
            
            <button
              onClick={handlePurchase}
              disabled={!connected || validFields.length === 0 || purchasing || !adminWallet}
              className={`btn-primary ${(!connected || validFields.length === 0 || !adminWallet) ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="purchase-ticket-btn"
            >
              <span className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                {purchasing ? 'Processing...' : `Pay ${totalSol.toFixed(4)} SOL`}
              </span>
            </button>
          </div>
        </motion.div>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="bg-[#0F172A] border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Confirm Purchase</DialogTitle>
              <DialogDescription className="text-white/60">
                You are about to buy a ticket with {validFields.length} field(s).
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className={`p-3 rounded-lg ${network === 'mainnet' ? 'bg-red-500/10 border border-red-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                <p className={`text-sm font-bold ${network === 'mainnet' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {network === 'mainnet' ? '⚠️ REAL MONEY - Mainnet' : '🧪 Test Mode - Devnet'}
                </p>
              </div>

              {validFields.map(field => (
                <div key={field.field_number} className="flex items-center justify-between">
                  <span className="text-white/60">Field {field.field_number}</span>
                  <div className="flex gap-1">
                    {field.selected_numbers.map(num => (
                      <span key={num} className="font-mono text-sol-green">{num}</span>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60">Your Balance</span>
                  <span className="font-mono text-sol-green">
                    {solBalance?.toFixed(4) || '--'} SOL
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/60">To Wallet</span>
                  <span className="font-mono text-xs text-white/60">
                    {adminWallet?.slice(0, 8)}...{adminWallet?.slice(-8)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold">Total</span>
                  <span className="font-heading text-xl text-sol-green">
                    {totalSol.toFixed(4)} SOL
                  </span>
                </div>
                <p className="text-white/40 text-xs mt-1 text-right">
                  ≈ ${totalUsdc.toFixed(2)} USD
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmPurchase}
                disabled={purchasing || (solBalance !== null && solBalance < totalSol)}
                className="bg-sol-purple hover:bg-sol-purple/80"
                data-testid="confirm-purchase-btn"
              >
                {purchasing ? 'Processing...' : 'Confirm & Pay'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Play;
